import '.cp{min-width:0px;}.ssr-plugin'
import '.ia{min-inline-size:0px;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.s{position:relative;}.ssr-plugin'
import '.gt{overflow-x:hidden;}.ssr-plugin'
import '.ib{flex:0 0 51px;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useLayoutEffect,
  useImperativeHandle,
} from 'react'
import {
  Badge,
  Button,
  capitalize,
  colors,
  Menu,
  Pagination,
  radius,
  splitByUppercase,
  Stack,
  useTheme,
  Virtualized,
  VirtualizedResetScroll,
} from '../../index.js'
import { Sort } from '../SortInput/index.js'
import { styled } from 'inlines'
import {
  BasedNode,
  UiSchema,
  UiSchemaProp,
  UiSchemaType,
} from '../../types/schema.js'
import { measureTextWidth, Text } from '../Text/index.js'
import { TableRow, ROW_HEIGHT, TableRowProps } from './TableRow.js'
import { useWindowResize } from '../../hooks/useWindowResize.js'
import { useDependency } from '../../hooks/useDependency.js'
import { ContextMenuItem } from '../ContextMenu/index.js'
import { TableCell } from './TableRow.js'
import { PartialOptions } from 'overlayscrollbars'

export type BulkAction = ContextMenuItem & {
  onClick?: (rows?: Set<number>) => void
}

export type RowAction = ContextMenuItem & {
  onClick?: (...values: any) => void
}

const TableContainer = forwardRef<HTMLDivElement, any>((props, ref) => (
  <styled.div className="cp ia f a i s"
    ref={ref}
    role={props.role || 'table'}
    {...props}
    style={{
      height: props?.style?.height || '100%',
      ...props?.style,
    }}
  />
))

export type TableItemsPosition = {
  first: number
  last: number
  actualFirst: number
  actualLast: number
}

export type TableResetScroll = VirtualizedResetScroll
export type TablePagination = Pagination

export type TableProps = Partial<
  Pick<
    TableRowProps,
    'checkboxVisibility' | 'actionsVisibility' | 'isHoverDisabled'
  >
> & {
  data: BasedNode | BasedNode[]
  schemaType: string
  schema: UiSchema
  containerRef?: React.RefObject<HTMLDivElement> | null
  scrollContainerRef?: (el: HTMLDivElement | null) => void
  checksum?: number
  sort?: Sort
  rowHeight?: 'regular' | 'large'
  colVisibility?: 'fit' | 'scroll'
  headerVisibility?: 'always' | 'never'
  colHidden?: Set<string>
  rowActions?: Record<string, RowAction> | Record<string, null>
  bulkActions?: Record<string, BulkAction> | Record<string, null>
  selectableText?: boolean
  disabled?: boolean
  showHideColumnButton?: boolean
  showSortingMenu?: boolean
  selectedRows?: Set<number>
  maxSelectedRows?: number
  disabledRows?: Set<number>
  outerBorder?: boolean
  scrollbars?: PartialOptions['scrollbars']
  forceHover?: number
  onSelectedRowsChange?: (rows: Set<number>) => void
  onSortChange?: React.Dispatch<React.SetStateAction<Sort>>
  onRowHover?: TableRowProps['onHover']
  onRowClick?: (values: BasedNode, value: any) => void
  onRowSelect?: (index: number, values: BasedNode) => void
  onAddNewItem?: () => void
  onColumnsChange?: (columns: UiSchemaType[]) => void
  onColumnHidden?: React.Dispatch<React.SetStateAction<Set<string>>>
  onColumnVisibilityChange?: (
    visibility: Record<'fit' | 'scroll', boolean>,
  ) => void
  pagination?: Pagination
}

export const Table = forwardRef<TableResetScroll, TableProps>(
  (
    {
      containerRef,
      scrollContainerRef,
      checksum,
      data,
      schemaType,
      schema,
      selectedRows: selectedRowsProp = new Set<number>(),
      maxSelectedRows,
      disabledRows: disabledRowsProp = new Set<number>(),
      rowHeight = 'regular',
      colVisibility: colVisibilityProp = 'fit',
      headerVisibility: headerVisibilityProp = 'always',
      colHidden: colHiddenProp = new Set<string>(),
      rowActions,
      bulkActions,
      checkboxVisibility: checkboxVisibilityProp = 'hover',
      actionsVisibility: actionsVisibilityProp = 'hover',
      isHoverDisabled,
      disabled,
      showHideColumnButton = false,
      showSortingMenu = true,
      sort: sortProp,
      selectableText = false,
      outerBorder,
      scrollbars,
      forceHover,
      onSortChange,
      onRowHover,
      onSelectedRowsChange,
      onRowClick,
      onRowSelect,
      onAddNewItem,
      onColumnsChange,
      onColumnHidden,
      onColumnVisibilityChange,
      pagination,
    },
    ref,
  ) => {
    const rows = Array.isArray(data) ? data : [data]

    const { theme } = useTheme()
    const { width } = useWindowResize()

    const tableContainerRef = useRef<HTMLDivElement | null>(null)
    const headerRef = useRef<any>(null)
    const virtualizedRef = useRef<TableResetScroll>(null)

    containerRef = containerRef || useRef(null)

    const [containerWidth, setContainerWidth] = useState(0)
    const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false)
    const [headerMenuState, setHeaderMenuState] =
      useState<Record<string, 'open' | 'closed'>>()

    const [colHidden, setColHidden] = useState<Set<string>>(colHiddenProp)
    const [selectedRows, setSelectedRows] =
      useState<Set<number>>(selectedRowsProp)
    const [sortState, setSortState] = useState<Sort>(sortProp)

    const sort = sortProp ?? sortState
    const colVisibility = colVisibilityProp
    const headerVisibility = headerVisibilityProp
    const colHiddenPropDep = useDependency(colHiddenProp)
    const selectedRowsPropDep = useDependency(selectedRowsProp)
    const sortPropDep = useDependency(sortProp)
    const prevColHiddenPropDep = useRef(colHiddenPropDep)
    const prevSelectedRowsPropDep = useRef(selectedRowsPropDep)
    const prevSortPropDep = useRef(sortPropDep)

    useEffect(() => {
      if (prevColHiddenPropDep.current === colHiddenPropDep) return
      prevColHiddenPropDep.current = colHiddenPropDep
      setColHidden(new Set(colHiddenProp))
    }, [colHiddenPropDep, colHiddenProp])

    useEffect(() => {
      if (prevSelectedRowsPropDep.current === selectedRowsPropDep) return
      prevSelectedRowsPropDep.current = selectedRowsPropDep

      setSelectedRows(selectedRowsProp)

      if (selectedRowsProp?.size === 0 && allRowsSelected) {
        setAllRowsSelected(false)
      }
    }, [selectedRowsPropDep, selectedRowsProp, allRowsSelected])

    useEffect(() => {
      if (prevSortPropDep.current === sortPropDep) return
      prevSortPropDep.current = sortPropDep

      setSortState(sortProp)

      if (allRowsSelected) {
        setAllRowsSelected(false)
      }
    }, [sortPropDep, sortProp, allRowsSelected])

    const rowsCount = useMemo(() => {
      let count = 0
      for (let i = 0; i < rows.length; i++) {
        if (i in rows) count++
      }
      return count
    }, [useDependency(rows), checksum, data])

    const paddingSize = 20
    const iconSize = 24
    const hasActions = Object.keys(rowActions || {}).length > 0
    const hasBulkActions = Object.keys(bulkActions || {}).length > 0
    const buttonsOffset =
      (checkboxVisibilityProp === 'never' ? 0 : 44) +
      ((!hasActions && !hasBulkActions) || actionsVisibilityProp === 'never'
        ? 0
        : 44) // 44 + 44 from the select checkbox and the options at the end of the line if they're present

    const typeSchemaRaw: UiSchemaType = useMemo(
      () =>
        schema?.[schemaType || '_root'] ||
        schema?.types?.[schemaType || '']?.props ||
        {},
      [useDependency(schema), schemaType],
    )

    const typeSchema: string[] = useMemo(
      () =>
        Object.entries(typeSchemaRaw)
          .filter(
            ([_key, value]: [string, UiSchemaProp]) =>
              Boolean(value) || value?.hide === true,
          )
          .map(([key]) => key),
      [useDependency(typeSchemaRaw)],
    )

    const columnKeys = useMemo(() => {
      return typeSchema.includes('id') ? typeSchema : ['id', ...typeSchema]
    }, [useDependency(typeSchema)])

    const columns = useMemo(() => {
      return columnKeys.map((key) => {
        if (key === 'id') {
          return {
            key,
            prop: {
              title: 'ID',
              render: (value: number) => (
                <TableCell>
                  <Badge leadIcon="number">{value}</Badge>
                </TableCell>
              ),
              ...(typeSchemaRaw[key] as UiSchemaProp),
            },
          }
        }

        return { key, prop: typeSchemaRaw[key] } as unknown as UiSchemaType
      }) as UiSchemaType[]
    }, [useDependency(columnKeys), useDependency(typeSchemaRaw)])

    const schemaHidden = useMemo(() => {
      const next = new Set<string>()
      for (const key of columnKeys) {
        if (typeSchemaRaw?.[key]?.hide) next.add(key)
      }
      return next
    }, [useDependency(columnKeys), useDependency(typeSchemaRaw)])

    const effectiveColHidden = useMemo(() => {
      const next = new Set<string>(colHidden)
      for (const key of schemaHidden) next.add(key)
      return next
    }, [useDependency(colHidden), useDependency(schemaHidden)])

    const filteredColumns = useMemo(() => {
      return columns.filter((column) => !effectiveColHidden.has(column.key))
    }, [useDependency(columns), useDependency(effectiveColHidden)])

    const { colWidths, colVisibilityOptions } = useMemo(() => {
      const cols = filteredColumns.length

      if (cols < 2 || !containerWidth) {
        return {
          colWidths: [] as number[],
          colVisibilityOptions: { fit: true, scroll: true },
        }
      }

      const containerWidthCalc = containerWidth - buttonsOffset || 0
      const base = Math.floor(containerWidthCalc / cols)

      let widths: number[] = []

      if (colVisibility === 'fit') {
        widths = Array(cols).fill(base)
      } else {
        widths = filteredColumns.map(({ key, prop }) => {
          if (prop.width) return prop.width

          const header = prop.title || key
          const content = rows?.[0]?.[key]

          const headerW =
            measureTextWidth(String(header).toUpperCase(), 'display-bold') +
            iconSize +
            paddingSize

          const contentW =
            measureTextWidth(String(content ?? ''), 'display-regular') +
            paddingSize

          const w = Math.ceil(Math.max(headerW, contentW))
          return w > 250 ? 250 : w
        })
      }

      const nextOptions =
        base > 100 ? { fit: true, scroll: true } : { fit: false, scroll: true }

      return { colWidths: widths, colVisibilityOptions: nextOptions }
    }, [
      useDependency(filteredColumns),
      useDependency(rows),
      checksum,
      containerWidth,
      colVisibility,
      buttonsOffset,
    ])

    const allIds = useMemo(() => {
      return new Set(rows.map((item: BasedNode) => item.id))
    }, [useDependency(rows)])

    const effectiveSelectedRows = allRowsSelected ? allIds : selectedRows

    const disabledRows = useMemo(() => {
      if (maxSelectedRows && effectiveSelectedRows.size === maxSelectedRows) {
        return new Set(
          rows
            .filter((item: BasedNode) => !effectiveSelectedRows.has(item.id))
            .map((item: BasedNode) => item.id),
        )
      }
      return disabledRowsProp
    }, [
      maxSelectedRows,
      useDependency(effectiveSelectedRows),
      useDependency(rows),
      useDependency(disabledRowsProp),
    ])

    const setSorting = useCallback(
      (key: string = filteredColumns[0]?.key, direction: 'asc' | 'desc') => {
        const next = { key, direction }
        setSortState(next)
        if (allRowsSelected) setAllRowsSelected(false)
        onSortChange?.(next)
      },
      [filteredColumns, allRowsSelected, onSortChange],
    )

    const headerColumns: UiSchemaType[] = useMemo(
      () =>
        filteredColumns.map((column, index) => {
          const columnType = column.key === 'id' ? 'number' : column.prop.type

          const sortingText: string[] = (() => {
            switch (columnType) {
              case 'alias':
              case 'text':
              case 'string':
                return ['A to Z', 'Z to A']
              case 'timestamp':
                return ['Earliest first', 'Latest first']
              case 'number':
                return ['0 to 9', '9 to 0']
              case 'boolean':
                return ['True first', 'False first']
              default:
                return ['A to Z', 'Z to A']
            }
          })()

          return {
            key: column.key,
            prop: {
              type: 'string',
              title: column.key,
              align: column.prop.align,
              render: (value) => {
                const label =
                  column?.key === 'id' || value?.length < 4
                    ? value
                    : capitalize(splitByUppercase(value))

                if (!showSortingMenu) {
                  return (
                    <TableCell
                      cursor="default"
                      width={colWidths[index]}
                      align={column.prop.align}
                    >
                      <Text
                        variant="subtext-medium"
                        maxWidth={colWidths[index]}
                        selectableText={false}
                      >
                        {label}
                      </Text>
                    </TableCell>
                  )
                }

                return (
                  <span className="f">
                    <Menu
                      position="bottom"
                      onClose={() =>
                        setHeaderMenuState({ [column.key]: 'closed' })
                      }
                    >
                      <Menu.Trigger>
                        <Button
                          leadIcon={
                            sort?.key === column.key &&
                            sort?.direction === 'asc'
                              ? 'arrow-down'
                              : sort?.key === column.key &&
                                  sort?.direction === 'desc'
                                ? 'arrow-up'
                                : null
                          }
                          width="full"
                          variant="ghost"
                          align={column.prop.align}
                          onClick={() =>
                            setHeaderMenuState({ [column.key]: 'open' })
                          }
                          forceHover={headerMenuState?.[column.key] === 'open'}
                        >
                          <Text
                            maxWidth={colWidths[index]}
                            singleLine
                            variant="subtext-medium"
                          >
                            {label}
                          </Text>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Items>
                        <Menu.Header>Sort by...</Menu.Header>
                        <Menu.Item
                          leadIcon="arrow-down"
                          onClick={() => setSorting(column.key, 'asc')}
                          label={sortingText[0]}
                        />
                        <Menu.Item
                          leadIcon="arrow-up"
                          onClick={() => setSorting(column.key, 'desc')}
                          label={sortingText[1]}
                        />
                        {sort?.key === column.key && (
                          <Menu.Item
                            leadIcon="close"
                            onClick={() => setSorting('id', sort?.direction)}
                            label="Clear sort"
                          />
                        )}
                        {filteredColumns.length > 1 && showHideColumnButton && (
                          <>
                            <Menu.Separator />
                            <Menu.Item
                              leadIcon="hide"
                              onClick={() => {
                                setColHidden((prev) => {
                                  const next = new Set(prev)
                                  next.add(column.key)
                                  return next
                                })
                              }}
                              label="Hide column"
                            />
                          </>
                        )}
                      </Menu.Items>
                    </Menu>
                  </span>
                )
              },
            },
          }
        }),
      [
        useDependency(filteredColumns),
        useDependency(colWidths),
        sort?.direction,
        sort?.key,
        headerMenuState,
        showSortingMenu,
        showHideColumnButton,
        setSorting,
      ],
    )

    const headerValues = useMemo(() => {
      return filteredColumns.reduce(
        (acc, col) => {
          return Object.assign(acc, {
            [col?.key]: capitalize(col?.prop?.title || col?.key),
          })
        },
        { id: -1 },
      )
    }, [useDependency(filteredColumns)])

    const rowWidth = useMemo(() => {
      if (!containerWidth) return 0
      if (colVisibility === 'fit') return containerWidth
      const sum = colWidths.reduce((acc, w) => acc + (w || 0), 0)
      const w = buttonsOffset + sum
      return Math.max(containerWidth, w)
    }, [containerWidth, colVisibility, colWidths, buttonsOffset])

    const itemSize = useCallback(() => {
      return {
        height: ROW_HEIGHT[rowHeight],
        width: rowWidth,
      }
    }, [rowHeight, rowWidth])

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if (!headerRef?.current) return
        const scrollLeft = (event?.currentTarget as HTMLElement).scrollLeft
        headerRef.current.scrollLeft = scrollLeft
        if (headerRef.current.firstElementChild?.scrollWidth) {
          headerRef.current.width =
            headerRef.current.firstElementChild.scrollWidth
        }
      },
      [],
    )

    const handleToggleSelectionRow = useCallback(
      (id: number, values: BasedNode) => {
        onRowSelect?.(id, values)

        const all = allRowsSelected
        const currentAllSet = allIds

        let nextAllRowsSelected = all
        let nextSelectedRows: Set<number>

        if (all) {
          nextSelectedRows = new Set(currentAllSet)
          nextSelectedRows.delete(id)
          nextAllRowsSelected = false
        } else {
          nextSelectedRows = new Set(selectedRows)

          if (nextSelectedRows.has(id)) {
            nextSelectedRows.delete(id)
          } else {
            if (!maxSelectedRows || selectedRows.size < maxSelectedRows) {
              nextSelectedRows.add(id)
            }
          }

          nextAllRowsSelected = nextSelectedRows.size === rowsCount
        }

        setSelectedRows(nextSelectedRows)
        if (nextAllRowsSelected !== allRowsSelected) {
          setAllRowsSelected(nextAllRowsSelected)
        }

        const emitted = nextAllRowsSelected
          ? new Set(currentAllSet)
          : nextSelectedRows

        onSelectedRowsChange?.(emitted)
      },
      [
        onRowSelect,
        onSelectedRowsChange,
        allRowsSelected,
        allIds,
        selectedRows,
        maxSelectedRows,
        rowsCount,
      ],
    )

    const defaultRowActions = rowActions

    const defaultBulkActions = useMemo(() => {
      if (!bulkActions) return undefined

      return Object.fromEntries(
        Object.entries(bulkActions).map(([key, action]) => {
          if (!action?.onClick) return [key, action] as const

          const handler = action.onClick
          return [
            key,
            {
              ...action,
              onClick: () => handler(effectiveSelectedRows),
            },
          ] as const
        }),
      ) as typeof bulkActions
    }, [useDependency(bulkActions), useDependency(effectiveSelectedRows)])

    useImperativeHandle(ref, () => ({
      resetScroll: () => virtualizedRef.current?.resetScroll(),
    }))

    useLayoutEffect(() => {
      if (!tableContainerRef.current) return
      setContainerWidth(
        tableContainerRef.current.getBoundingClientRect().width || 0,
      )
    }, [width])

    useLayoutEffect(() => {
      if (!headerRef?.current) return
      headerRef.current.scrollLeft = 0
    }, [rowWidth, checksum])

    const colsSig = useMemo(() => {
      return filteredColumns.map((c) => c.key).join('|')
    }, [useDependency(filteredColumns)])

    const hiddenSig = useMemo(() => {
      return [...effectiveColHidden].sort().join('|')
    }, [useDependency(effectiveColHidden)])

    const prevColsSig = useRef<string>('')
    const prevHiddenSig = useRef<string>('')

    useEffect(() => {
      if (prevColsSig.current !== colsSig) {
        prevColsSig.current = colsSig
        onColumnsChange?.(filteredColumns)
      }
      if (prevHiddenSig.current !== hiddenSig) {
        prevHiddenSig.current = hiddenSig
        onColumnHidden?.(effectiveColHidden)
      }
    }, [
      colsSig,
      hiddenSig,
      onColumnsChange,
      onColumnHidden,
      filteredColumns,
      effectiveColHidden,
    ])

    const prevVisRef = useRef<{ fit: boolean; scroll: boolean } | null>(null)

    useEffect(() => {
      const prev = prevVisRef.current
      if (
        !prev ||
        prev.fit !== colVisibilityOptions.fit ||
        prev.scroll !== colVisibilityOptions.scroll
      ) {
        prevVisRef.current = colVisibilityOptions
        onColumnVisibilityChange?.(colVisibilityOptions)
      }
    }, [
      colVisibilityOptions.fit,
      colVisibilityOptions.scroll,
      onColumnVisibilityChange,
    ])

    return (
      <TableContainer
        ref={tableContainerRef}
        aria-rowcount={rows.length + 1}
        style={{
          border: outerBorder && `1px solid ${colors.neutral20Adjusted}`,
          borderRadius: outerBorder && radius[20],
        }}
      >
        {headerVisibility === 'always' &&
          rowsCount > 0 &&
          headerColumns.length > 0 && (
            <div className="gt ib a d f"
              ref={headerRef}
              style={{
                borderBottom: `1px solid ${colors.neutral10}`,
              }}
            >
              <TableRow
                rowData={headerValues}
                columns={headerColumns}
                widths={colWidths}
                rowHeight="regular"
                onRowClick={null}
                selectableText={false}
                disabled={disabled}
                rowRole="rowheader"
                cellRole="columnheader"
                checkboxVisibility={
                  checkboxVisibilityProp !== 'never' ? 'aways' : 'never'
                }
                actionsVisibility={
                  typeof effectiveSelectedRows !== 'string' &&
                  effectiveSelectedRows?.size > 1
                    ? 'aways'
                    : 'never'
                }
                variant="header"
                selected={rowsCount && allRowsSelected}
                rowActions={defaultBulkActions}
                onRowSelect={() => {
                  if (allRowsSelected) {
                    setAllRowsSelected(false)
                    const next = new Set<number>()
                    setSelectedRows(next)
                    onSelectedRowsChange?.(next)
                  } else {
                    setAllRowsSelected(true)
                    const next = new Set(allIds)
                    setSelectedRows(next)
                    onSelectedRowsChange?.(next)
                  }
                }}
              />
            </div>
          )}

        {!rowsCount && (
          <Stack
            style={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              bottom: 0,
              zIndex: 2,
            }}
            direction="column"
            justify="center"
            align="center"
            gap={16}
          >
            <Stack direction="column" justify="center" align="center" gap={8}>
              <Text variant="subheading-medium" color="neutral80">
                No items
              </Text>
              <Text variant="display-regular" color="neutral60">
                There are no items here - yet.
              </Text>
            </Stack>
            {onAddNewItem && (
              <Button variant="border" leadIcon="add" onClick={onAddNewItem}>
                <Text variant="display-medium" color="neutral80">
                  Add {schemaType || 'item'}
                </Text>
              </Button>
            )}
          </Stack>
        )}

        <Virtualized
          ref={virtualizedRef}
          onViewportChange={scrollContainerRef}
          key={checksum}
          values={rows}
          pagination={pagination || { total: rows.length, type: 'scroll' }}
          scrollbars={{
            theme: theme === 'light' ? 'os-theme-dark' : 'os-theme-light',
            ...scrollbars,
          }}
          itemSize={itemSize}
          onScroll={handleScroll}
        >
          {({ values }) => {
            return (
              <div className="f" role="rowgroup">
                {values.map((value) => {
                  return (
                    <TableRow
                      key={value?.id}
                      index={value?.id}
                      rowData={value}
                      allRowsData={rows}
                      columns={filteredColumns}
                      widths={colWidths}
                      isHoverDisabled={isHoverDisabled}
                      isHovered={forceHover === value?.id}
                      disabled={disabledRows?.has(value?.id) || disabled}
                      rowHeight={rowHeight}
                      rowActions={defaultRowActions}
                      checkboxVisibility={checkboxVisibilityProp}
                      actionsVisibility={actionsVisibilityProp}
                      onRowClick={onRowClick}
                      selectableText={selectableText}
                      selected={
                        effectiveSelectedRows?.has(value?.id) || allRowsSelected
                      }
                      onRowSelect={handleToggleSelectionRow}
                      onHover={onRowHover}
                    />
                  )
                })}
              </div>
            )
          }}
        </Virtualized>
      </TableContainer>
    )
  },
)

export { TableRow, TableCell }
